// AuthContext - current-user session state: refresh/login/logout + bootstrap gate.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { resetStorageInfo } from '@/context/UploadContext';
import { ApiError, api, type User } from '@/lib/api';

interface AuthState {
  user: User | null;
  loading: boolean;
  // No admin user exists yet → route to first-user setup, not /login.
  // Only meaningful while user === null.
  needsBootstrap: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [loading, setLoading] = useState(true);

  // Auth identity has five writers - the mount probe, the foreground probe
  // below, login, logout, and the session-expired listener - and /me is a round
  // trip, so an attempt that started earlier can resolve later. Last-resolver-
  // wins is wrong in both directions: a stale success reinstates a session a
  // newer 401 (or an explicit logout) already ended, and a stale 401 signs out
  // a session a newer login just established. Each writer claims a generation
  // and commits only while it is still the newest.
  //
  // Explicit intent - logout and session expiry - does not merely claim a
  // generation, it re-claims one as it commits, so a probe that started while
  // it was in flight cannot undo it either.
  const authGen = useRef(0);
  const claimAuthGen = useCallback(() => {
    authGen.current += 1;
    return authGen.current;
  }, []);

  const refresh = useCallback(async () => {
    const gen = claimAuthGen();
    const newest = () => gen === authGen.current;
    try {
      const me = await api.me();
      if (!newest()) return;
      setUser(me);
      // If we have a user, bootstrap is definitionally complete.
      setNeedsBootstrap(false);
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'unauthorized') {
        if (!newest()) return;
        setUser(null);
        // No session - check if any user exists yet. A failed probe means
        // "not needed" so a transient DB blip can't surface the setup page.
        try {
          const status = await api.bootstrapStatus();
          if (!newest()) return;
          setNeedsBootstrap(status.needs_bootstrap);
        } catch {
          if (!newest()) return;
          setNeedsBootstrap(false);
        }
      } else {
        console.error(e);
      }
    } finally {
      // Gated like the rest: a superseded mount probe clearing this would render
      // the app as anonymous - a /login flash - while the newer probe is still
      // in flight. Every writer that supersedes a probe clears it instead, so
      // "loading always resolves" holds without depending on this attempt.
      if (newest()) setLoading(false);
    }
  }, [claimAuthGen]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Global 401 handling: the api request() wrapper fires this on any 401
  // (session revoked server-side via session_version bump, logout in another
  // tab, deactivation). Clearing the user makes the route guard render
  // /login immediately instead of leaving a dead session's UI up until the
  // next manual navigation.
  useEffect(() => {
    const onExpired = () => {
      claimAuthGen();
      setUser(null);
      setLoading(false);
      resetStorageInfo();
    };
    window.addEventListener('voltz:session-expired', onExpired);
    return () => window.removeEventListener('voltz:session-expired', onExpired);
  }, [claimAuthGen]);

  // A tab backgrounded across a deploy - or simply for hours - can still hold a
  // user object whose session died meanwhile. TanStack Query's focus refetch
  // revalidates DATA, not the session, so nothing establishes that the session
  // is still good until some widget query happens to run; until then a dead
  // session's UI stays up and every widget reports its own generic failure.
  // Re-probe when the tab returns to the foreground: a 401 here travels the
  // request() session-expired path and the route guard renders /login.
  const authed = user !== null;
  useEffect(() => {
    if (!authed) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [authed, refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const gen = claimAuthGen();
    const res = await api.login(email, password);
    // A logout that landed while these credentials were in flight is the newer
    // intent; honour it rather than signing the user back in.
    if (gen !== authGen.current) return;
    setUser(res);
    setNeedsBootstrap(false);
    setLoading(false);
  }, [claimAuthGen]);

  const logout = useCallback(async () => {
    claimAuthGen();
    try {
      await api.logout();
    } finally {
      // Re-claimed, not checked: the cookie is gone, so a probe that started
      // while we were logging out must not restore the session it read.
      claimAuthGen();
      setUser(null);
      // Logging out doesn't suddenly mean bootstrap is needed -
      // there's still an admin in the DB, just no session for it.
      setNeedsBootstrap(false);
      setLoading(false);
      // Drop the module-level storage-info memo so the next session re-probes
      // the backend instead of inheriting this one's routing (FE-SEC-01).
      resetStorageInfo();
    }
  }, [claimAuthGen]);

  const value = useMemo<AuthState>(
    () => ({ user, loading, needsBootstrap, refresh, login, logout }),
    [user, loading, needsBootstrap, refresh, login, logout]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
